import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { Calculator, CheckCircle2, Play, RefreshCcw, XCircle, AlertTriangle } from "lucide-react";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import { postRequest, API_BASE_URL, extractErrorMessage } from "../../../../api";
import EmployeeMultiSelect from "./EmployeeMultiSelect";
import "./PaieCyclePages.css";

const userNameCalc = (user) => user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username || `#${user?.id}`;

const isEarlierPeriod = (candidate, period) =>
    candidate.annee < period.annee || (candidate.annee === period.annee && candidate.mois < period.mois);

const CalculPaiePage = () => {
    const dispatch = useDispatch();
    const payroll = useSelector((state) => state.payroll || {});
    const users = useSelector((state) => state.user?.users || []);
    const [form, setForm] = useState({ periodeId: "", userIds: [], nombreJoursTravailles: 26, modePaiement: "VIREMENT" });
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState([]);
    const [report, setReport] = useState(null);
    const [calculating, setCalculating] = useState(false);

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("periodes"));
    }, [dispatch]);

    const activeUsers = useMemo(() => {
        return users.filter(u => u.isActive !== false);
    }, [users]);

    const selectedPeriod = useMemo(
        () => (payroll.periodes || []).find((period) => String(period.id) === String(form.periodeId)),
        [payroll.periodes, form.periodeId]
    );

    const hasUnclosedPreviousPeriod = (period) =>
        (payroll.periodes || []).some((candidate) =>
            isEarlierPeriod(candidate, period) && candidate.statut !== "CLOTUREE"
        );

    const calculate = async () => {
        if (!form.periodeId) {
            Swal.fire({ icon: "warning", text: "Sélectionnez une période." });
            return;
        }
        if (selectedPeriod?.statut === "CLOTUREE") {
            Swal.fire({ icon: "warning", text: "Cette période est clôturée et ne peut plus être calculée." });
            return;
        }
        if (selectedPeriod && hasUnclosedPreviousPeriod(selectedPeriod)) {
            Swal.fire({ icon: "warning", text: "Clôturez d'abord la période précédente avant de traiter celle-ci." });
            return;
        }

        setProgress(0);
        setLogs([]);
        setReport(null);
        setCalculating(true);

        const token = sessionStorage.getItem("token") || localStorage.getItem("token");

        const selectedUserIdSet = new Set(form.userIds.map(String));
        const targetUsersList = selectedUserIdSet.size === 0
            ? activeUsers
            : activeUsers.filter((user) => selectedUserIdSet.has(String(user.id)));
        if (targetUsersList.length === 0) {
            setLogs([{ type: "error", text: "Aucun collaborateur actif trouvé dans l'entreprise." }]);
            setReport({ total: 0, successCount: 0, failCount: 0, completed: true });
            setCalculating(false);
            return;
        }

        setReport({ total: targetUsersList.length, successCount: 0, failCount: 0, completed: false });
        setLogs([{ type: "info", text: `Initialisation du calcul pour ${targetUsersList.length} collaborateur(s)...` }]);

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < targetUsersList.length; i++) {
            const user = targetUsersList[i];
            const name = userNameCalc(user);

            try {
                const response = await postRequest({
                    url: `${API_BASE_URL}/api/bulletins-paie/calculate`,
                    data: {
                        userId: user.id,
                        periodeId: Number(form.periodeId),
                        nombreJoursTravailles: Number(form.nombreJoursTravailles),
                        modePaiement: form.modePaiement,
                    },
                    token
                });
                successCount++;
                setReport({ total: targetUsersList.length, successCount, failCount, completed: false });
                setLogs(prev => [
                    ...prev,
                    { type: "success", text: `${name} : Bulletin calculé avec succès. Net : ${Number(response.data?.netAPayer || 0).toLocaleString("fr-FR")} MAD.` }
                ]);
            } catch (error) {
                failCount++;
                const msg = extractErrorMessage(error, "Erreur");
                setReport({ total: targetUsersList.length, successCount, failCount, completed: false });
                setLogs(prev => [
                    ...prev,
                    { type: "error", text: `${name} : Échec du calcul - ${msg}.` }
                ]);
            }

            setProgress(Math.round(((i + 1) / targetUsersList.length) * 100));
        }

        setLogs(prev => [
            ...prev,
            { type: "info", text: `Traitement terminé. Succès: ${successCount}, Échecs: ${failCount}.` }
        ]);
        setReport({ total: targetUsersList.length, successCount, failCount, completed: true });

        Swal.fire({
            icon: failCount > 0 ? "warning" : "success",
            title: "Calcul global terminé",
            text: `${successCount} bulletin(s) calculé(s) avec succès. ${failCount} échec(s).`
        });

        setCalculating(false);
    };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><Calculator size={18} />Calcul de la paie</h2>
                    <p>Lancement du calcul par période, pour tous les collaborateurs ou une sélection ciblée.</p>
                </div>
                <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => dispatch(payrollFetchResourceRequest("periodes"))}>
                    <RefreshCcw size={15} /> Actualiser
                </button>
            </div>
            
            <div className="pcyc-grid pcyc-grid-form pcyc-grid-calculation">
                <section className="pcyc-card">
                    <div className="pcyc-card-title"><Play size={16} />Paramètres de calcul</div>
                    <div className="pcyc-form-grid">
                        <label>
                            <span>Période</span>
                            <select value={form.periodeId} onChange={(e) => setForm({ ...form, periodeId: e.target.value })} disabled={calculating}>
                                <option value="">Sélectionner</option>
                                {(payroll.periodes || []).map((p) => {
                                    const blockedByPreviousPeriod = hasUnclosedPreviousPeriod(p);
                                    return (
                                    <option key={p.id} value={p.id} disabled={p.statut === "CLOTUREE" || blockedByPreviousPeriod}>
                                        {String(p.mois).padStart(2, "0")}/{p.annee} - {p.statut} {p.statut === "CLOTUREE" ? "(Déjà clôturée)" : blockedByPreviousPeriod ? "(Période précédente à clôturer)" : ""}
                                    </option>
                                    );
                                })}
                            </select>
                        </label>
                        <EmployeeMultiSelect users={activeUsers} value={form.userIds} onChange={(userIds) => setForm({ ...form, userIds })} disabled={calculating} />
                        <label>
                            <span>Jours travaillés (Défaut)</span>
                            <input type="number" step="0.01" value={form.nombreJoursTravailles} onChange={(e) => setForm({ ...form, nombreJoursTravailles: e.target.value })} disabled={calculating} />
                        </label>
                        <label>
                            <span>Mode paiement (Défaut)</span>
                            <select value={form.modePaiement} onChange={(e) => setForm({ ...form, modePaiement: e.target.value })} disabled={calculating}>
                                <option value="VIREMENT">VIREMENT</option>
                                <option value="CHEQUE">CHEQUE</option>
                                <option value="ESPECES">ESPECES</option>
                            </select>
                        </label>
                    </div>
                    <div className="pcyc-actions">
                        <button className="pcyc-btn pcyc-btn-primary" type="button" onClick={calculate} disabled={calculating}>
                            <Play size={15} /> Lancer le calcul
                        </button>
                    </div>
                </section>
                
                <section className="pcyc-card">
                    <div className="pcyc-card-title">Rapport de calcul</div>
                    {!report ? (
                        <div className="pcyc-calculation-empty">
                            <div><Calculator size={26} /></div>
                            <strong>Le résultat du calcul apparaîtra ici</strong>
                            <span>Sélectionnez les collaborateurs puis lancez le calcul.</span>
                        </div>
                    ) : (
                        <>
                            <div className="pcyc-calculation-stats">
                                <div className="pcyc-calculation-stat"><strong>{report.total}</strong><span>Ciblés</span></div>
                                <div className="pcyc-calculation-stat is-success"><strong>{report.successCount}</strong><span>Calculés</span></div>
                                <div className="pcyc-calculation-stat is-error"><strong>{report.failCount}</strong><span>Erreurs</span></div>
                            </div>
                            <div className="pcyc-calculation-progress-head">
                                <span>{report.completed ? "Calcul terminé" : "Calcul en cours"}</span>
                                <strong>{progress}%</strong>
                            </div>
                            <div className="pcyc-progress"><span style={{ width: `${progress}%` }} /></div>
                            {report.completed && (
                                <div className={`pcyc-calculation-alert ${report.failCount > 0 ? "is-warning" : "is-success"}`}>
                                    {report.failCount > 0 ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}
                                    <span>{report.failCount > 0 ? `${report.successCount} calcul(s) réussi(s), ${report.failCount} en erreur.` : `Les ${report.successCount} calcul(s) ont été effectués avec succès.`}</span>
                                </div>
                            )}
                            <div className="pcyc-calculation-log-list">
                                {logs.map((log, index) => (
                                    <div key={index} className={`pcyc-calculation-log is-${log.type}`}>
                                        {log.type === "success" && <CheckCircle2 size={14} />}
                                        {log.type === "error" && <XCircle size={14} />}
                                        {log.type === "info" && <RefreshCcw size={14} className={calculating ? "kms-spin" : ""} />}
                                        <span>{log.text}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default CalculPaiePage;
